import { ReactElement } from "react";
import stringify from "fast-json-stable-stringify";

import { groupBy } from "@/lib/groupBy";
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ExpandedState,
  Row,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

import { simpleHash } from "./datatable-utils/simple-hash";

export interface DatatableState {
  showColumnHeaders: boolean;
  sectionColumnId?: string | null | undefined;
  subSectionColumnId?: string | null | undefined;

  // tanstack table state
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  columnOrder: ColumnOrderState;
  columnVisibility: VisibilityState;
  sorting: SortingState;
  expanded: ExpandedState;
}

export const defaultDatatableState: DatatableState = {
  showColumnHeaders: true,
  sectionColumnId: null,
  subSectionColumnId: null,

  globalFilter: "",
  columnFilters: [],
  columnOrder: [],
  columnVisibility: {},
  sorting: [],
  expanded: true,
};
export type DatatableRowData = object;
export type DatatableRowDataWithCacheKey<T extends DatatableRowData> = T & {
  __cacheKey: string;
};

export type DatatableFilterConfigurationViewType = "none" | "popover" | "modal";

export interface DatatableColumn<TData extends DatatableRowData, TValue = unknown> {
  columnId: string;

  // the order for the column among all columns in the table ascending from left to right
  getOrderValue: () => number;

  // the default visibility state of the column (visible or hidden by default)
  getDefaultVisibilityState: () => boolean;

  // the column definition for the column: This is where the column is defined for react-table
  // here you define the column's id, accessorKey, enableHiding, enableSorting, enableColumnFilter, sortingFn, filterFn, header, cell, etc.
  getColumnDef: () => ColumnDef<TSectionData<TData>, TValue>;

  // The name of the column as it is displayed to the user (e.g. when toggling column visibility)
  getDisplayValue: () => string;

  // The view rendered to display the column in the list of available filters
  renderDisplayView: () => ReactElement;

  // Whether to render the filter configuration view in a modal or dropdown
  getFilterConfigurationViewType: () => DatatableFilterConfigurationViewType;

  // The view to render (in the modal or dropdown) where the user enters or selects the filter value(s)
  renderFilterConfigurationView: (onFinished: () => void) => ReactElement | null;

  // The view to render in the list of applied filters
  renderAppliedFilterView: () => ReactElement;

  // Whether to enable sectioning for the column
  enableSectioning?: boolean;

  // The key to use for sectioning the column
  // If not provided, the column will not be sectioned
  getSectionKey?: (data: TData) => string | string[];

  // If you want a specific sort order for the sections (e.g. for priority High, Medium, Low, ...)
  sortSection?: (a: SectionData<TData>, b: SectionData<TData>) => number;

  // If you want a specific sort order for the sub sections (e.g. for priority High, Medium, Low, ...)
  sortSubSection?: (a: SubSectionData<TData>, b: SubSectionData<TData>) => number;

  // The view to render in the section header
  renderSectionHeader?: (data: SectionData<TData>, row: Row<SectionData<TData>>) => ReactElement;

  // The view to render in the double section header
  renderSubSectionHeader?: (data: SubSectionData<TData>, row: Row<SubSectionData<TData>>) => ReactElement;

  // The function to resolve the state from the url params on initial load
  // Leave blank if the column does not support url params
  resolveStateFromUrlParams?: (urlParams: URLSearchParams) => Partial<DatatableState> | undefined;

  // Additional URL params to clean during URL sync (e.g. legacy params like "milestoneId")
  // These params are removed from the URL whenever the datatable syncs its filter state
  urlParamListToClean?: string[];
}

export interface SectionData<TData extends DatatableRowData> {
  __cacheKey: string;
  columnId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: DatatableColumn<TData, any>;
  key: string;
  data: TData[] | SubSectionData<TData>[];
  getSectionKey: (data: TData) => string | string[];
}

export interface SubSectionData<TData extends DatatableRowData> {
  __cacheKey: string;
  columnId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: DatatableColumn<TData, any>;
  key: string;
  data: TData[];
  getSubSectionKey: (data: TData) => string | string[];
}

export type TSectionData<TData extends DatatableRowData> = TData | SectionData<TData> | SubSectionData<TData>;

export function groupRowsBySection<TData extends DatatableRowData>(
  data: TData[],
  columnsConfig: DatatableColumn<TData>[],
  sectionColumnId: string,
  subSectionColumnId?: string | null,
): TSectionData<TData>[] {
  const sectionColumn = columnsConfig.find((c) => c.columnId === sectionColumnId);
  if (!sectionColumn?.enableSectioning || !sectionColumn.getSectionKey) {
    return data;
  }

  const getSectionKey = sectionColumn.getSectionKey;

  const sectionGroups: SectionData<TData>[] = Array.from(groupBy(data, getSectionKey).entries())
    .map(([key, items]) => {
      // Deduplicate items by id within each section to ensure uniqueness
      const uniqueItems = Array.from(
        new Map(items.map((item) => [("id" in item ? item.id : stringify(item)) as string, item])).values(),
      );

      const section = {
        columnId: sectionColumnId,
        key,
        column: sectionColumn,
        data: uniqueItems,
        getSectionKey,
      };

      const cacheKey = simpleHash(stringify(section));

      return {
        ...section,
        __cacheKey: cacheKey,
      };
    })
    .sort((a, b) => {
      if (!sectionColumn.sortSection) {
        return 0;
      }
      return sectionColumn.sortSection(a, b);
    });

  if (!subSectionColumnId) {
    return sectionGroups;
  }

  const subSectionColumn = columnsConfig.find((c) => c.columnId === subSectionColumnId);
  if (!subSectionColumn?.enableSectioning || !subSectionColumn.getSectionKey) {
    return sectionGroups;
  }

  const getSubSectionKey = subSectionColumn.getSectionKey;

  const sectionGroupsWithSubSections: SectionData<TData>[] = sectionGroups.map((section) => {
    const subGroups: SubSectionData<TData>[] = Array.from(groupBy(section.data as TData[], getSubSectionKey).entries())
      .map(([subKey, subItems]) => {
        // Deduplicate items by id within each subsection to ensure uniqueness
        const uniqueSubItems = Array.from(
          new Map(subItems.map((item) => [("id" in item ? item.id : stringify(item)) as string, item])).values(),
        );

        const subSection = {
          columnId: subSectionColumnId,
          key: subKey,
          column: subSectionColumn, // Still a DatatableColumn<TData>, safe for all rows
          data: uniqueSubItems,
          getSubSectionKey,
        };

        const cacheKey = simpleHash(stringify(subSection));

        return {
          ...subSection,
          __cacheKey: cacheKey,
        };
      })
      .sort((a, b) => {
        if (!subSectionColumn.sortSubSection) {
          return 0;
        }
        return subSectionColumn.sortSubSection(a, b);
      });

    return {
      columnId: section.columnId,
      key: section.key,
      column: section.column, // Still a DatatableColumn<TData>
      data: subGroups, // SubSectionData<TData>[]
      getSectionKey,
      __cacheKey: section.__cacheKey,
    };
  });

  return sectionGroupsWithSubSections;
}

export function isRawRow<TData extends DatatableRowData>(
  row: TSectionData<TData> | TSectionData<DatatableRowDataWithCacheKey<TData>>,
): row is TData {
  return row !== null && typeof row === "object" && !("data" in row);
}

export function isSectionRow<TData extends DatatableRowData>(
  row: TSectionData<TData> | TSectionData<DatatableRowDataWithCacheKey<TData>>,
): row is SectionData<TData> {
  return row !== null && typeof row === "object" && "data" in row && Array.isArray(row.data);
}

export function isSubSectionRow<TData extends DatatableRowData>(
  row: TSectionData<TData> | TSectionData<DatatableRowDataWithCacheKey<TData>>,
): row is SubSectionData<TData> {
  return (
    row !== null &&
    typeof row === "object" &&
    "data" in row &&
    Array.isArray(row.data) &&
    row.data.length > 0 &&
    row.data[0] !== null &&
    typeof row.data[0] === "object" &&
    !("data" in row.data[0])
  );
}
